/**
 * AuthBrandPanel — the ✨ left-side marketing pane used across Login,
 * Signup and Forgot-Password. Rich SociBaba brand language: orange +
 * fuchsia + cyan gradients, an animated SibaScore arc (that literally
 * counts up to 884 on mount), a live-activity ticker, and the honest
 * trust-markers strip.
 *
 * Variant modes:
 *   - "signup"  → pull-content · benefits + social proof to convert
 *   - "login"   → welcome-back · SibaScore of the week + activity feed
 *   - "forgot"  → memorable brand · calming reassurance + logo hero
 *
 * Kept intentionally self-contained (no external context, no data
 * fetches) so the auth pages stay fast to render.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  ShoppingBag,
  BrainCircuit,
  ShieldCheck,
  Check,
  Zap,
  Store,
  Video,
  MapPin,
  BadgeCheck,
  Star,
  MessageCircleHeart,
} from "lucide-react";
import { BrandMark } from "@/components/shared/BrandMark";

export type AuthPanelVariant = "signup" | "login" | "forgot";

// Live ticker rows — a scripted mix of buyer / seller / creator moments
// so first-time visitors immediately grok what happens inside SociBaba.
const LIVE_ROWS = [
  { icon: ShoppingBag, tint: "bg-emerald-500/20 text-emerald-300", text: "Shopper compared 4 headphones · picked ₹3,899 verified seller" },
  { icon: BrainCircuit, tint: "bg-fuchsia-500/20 text-fuchsia-300", text: "Sancta™ answered 12 shopping questions in Tamil, Hindi & English" },
  { icon: Store,        tint: "bg-orange-500/20 text-orange-300",  text: "Intent Radar caught 3 hot buyers for a Bengaluru fashion brand" },
  { icon: Video,        tint: "bg-rose-500/20 text-rose-300",      text: "Creator got a lead from an Instagram DM auto-classified as intent" },
  { icon: BadgeCheck,   tint: "bg-cyan-500/20 text-cyan-300",      text: "New product scored 912/1000 · Excellent · Buy with confidence" },
];

// Animated count-up hook used for the SibaScore visualisation.
function useCountUp(target: number, active: boolean, ms = 2200): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!active) { setV(0); return; }
    const t0 = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / ms);
      setV(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, ms]);
  return v;
}

// ── SibaScore hero widget ─────────────────────────────────────────────
const SibaScoreHero: React.FC<{ label?: string }> = ({ label = "Today's SibaScore" }) => {
  const value = useCountUp(884, true, 2400);
  const pct = Math.min(1, value / 1000);
  const r = 78, c = 2 * Math.PI * r;
  const off = c * (1 - pct);
  return (
    <div className="relative flex items-center gap-4">
      <div className="relative w-32 h-32 sm:w-36 sm:h-36 shrink-0">
        <svg viewBox="-100 -100 200 200" className="w-full h-full -rotate-90">
          <circle cx="0" cy="0" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
          <motion.circle
            cx="0" cy="0" r={r} fill="none" stroke="url(#sb-hero-grad)"
            strokeWidth="10" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={off}
          />
          <defs>
            <linearGradient id="sb-hero-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#d946ef" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-[9px] uppercase tracking-widest text-white/50">SibaScore™</div>
          <div className="font-display text-3xl sm:text-4xl font-bold tabular-nums leading-none bg-gradient-to-br from-orange-400 to-fuchsia-400 bg-clip-text text-transparent">
            {value}
          </div>
          <div className="text-[9px] text-white/50 mt-0.5">/ 1000</div>
        </div>
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}</div>
        <div className="font-display text-lg font-semibold text-white leading-tight">
          Bose QuietComfort<br />Ultra Earbuds
        </div>
        <div className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-2.5 py-0.5">
          <Check className="h-3 w-3 text-emerald-300" />
          <span className="text-[11px] text-emerald-200 font-semibold">Excellent · Buy with confidence</span>
        </div>
      </div>
    </div>
  );
};

// ── Live activity ticker ──────────────────────────────────────────────
const LiveTicker: React.FC = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % LIVE_ROWS.length), 3200);
    return () => clearInterval(t);
  }, []);
  const row = LIVE_ROWS[i];
  const Icon = row.icon;
  return (
    <div className="relative rounded-xl bg-white/[0.05] backdrop-blur-md border border-white/10 p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50 mb-2">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        Live · right now on SociBaba
      </div>
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex items-start gap-2.5"
      >
        <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${row.tint}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-[12.5px] text-white/85 leading-snug pt-0.5">{row.text}</div>
      </motion.div>
    </div>
  );
};

// ── Trust markers (honest — no fake press) ────────────────────────────
const TrustMarkers: React.FC = () => (
  <div className="grid grid-cols-3 gap-2">
    {[
      { label: "Made in India", sub: "Bengaluru HQ", icon: MapPin },
      { label: "GST Registered", sub: "29ABDCS7875E1ZU", icon: BadgeCheck },
      { label: "GDPR Ready", sub: "Data-deletion API", icon: ShieldCheck },
    ].map((m) => (
      <div key={m.label} className="rounded-lg bg-white/[0.04] border border-white/10 px-2.5 py-2">
        <m.icon className="h-3.5 w-3.5 text-white/70 mb-1" />
        <div className="text-[11px] font-semibold text-white leading-tight">{m.label}</div>
        <div className="text-[9px] text-white/50 leading-tight mt-0.5">{m.sub}</div>
      </div>
    ))}
  </div>
);

// ── Benefit trio (used on signup) ─────────────────────────────────────
const BENEFITS = [
  { icon: BrainCircuit, tint: "from-orange-500 to-amber-500",   title: "SibaScore™",  body: "One honest number, 0-1000, computed live from 11 signals. No stars. No paid opinions." },
  { icon: ShoppingBag,  tint: "from-fuchsia-500 to-rose-500",   title: "AI Cart™",    body: "Paste any marketplace URL. We surface a verified seller at a better price." },
  { icon: MessageCircleHeart, tint: "from-cyan-500 to-emerald-500", title: "Sancta™ AI", body: "Multilingual shopping agent — asks, remembers, and drops you into a cart in seconds." },
];

// ── Main component ────────────────────────────────────────────────────
export const AuthBrandPanel: React.FC<{ variant: AuthPanelVariant }> = ({ variant }) => {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-[#0a0a1a] via-[#1a0a2a] to-[#2a0f1a] text-white p-8 lg:p-12 xl:p-14 flex flex-col justify-between min-h-[560px] lg:min-h-screen">
      {/* Ambient orbs */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-fuchsia-500/25 blur-3xl animate-pulse" style={{ animationDuration: "6s" }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-orange-500/25 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full bg-cyan-400/10 blur-3xl" />
      {/* Dotted grid overlay */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)", backgroundSize: "22px 22px" }} />

      {/* Logo */}
      <div className="relative z-10 flex items-center justify-between mb-6">
        <Link to="/" data-testid="auth-panel-logo" className="flex items-center gap-2.5 group">
          <BrandMark
            showIcon
            tone="onDark"
            iconClassName="h-9 w-9 mr-2 inline-block align-middle invert group-hover:scale-110 transition-transform"
            className="font-serif-display text-2xl font-bold tracking-tight flex items-center"
            markClassName="text-[0.45em] align-super ml-[1px] text-white/60 font-normal"
          />
        </Link>
        <div className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-2.5 py-1 text-[10px] uppercase tracking-widest text-emerald-200">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
          </span>
          Live in prod
        </div>
      </div>

      {/* Variant-specific hero */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative z-10 space-y-8">
        {variant === "signup" && (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80 mb-3">Join the truth-first economy</div>
              <h2 className="font-display text-3xl sm:text-4xl xl:text-[2.75rem] font-bold tracking-tight leading-[1.05]">
                Buy with <span className="bg-gradient-to-br from-orange-400 to-fuchsia-400 bg-clip-text text-transparent">truth</span>.
                <br />Sell with <span className="bg-gradient-to-br from-cyan-400 to-emerald-400 bg-clip-text text-transparent">proof</span>.
              </h2>
              <p className="text-white/70 max-w-md leading-relaxed mt-4 text-[14px]">
                SociBaba™ reads eleven live signals across the web and hands shoppers, creators and brands one honest number for every product. Free to search, forever.
              </p>
            </div>
            <div className="space-y-3">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex items-start gap-3 rounded-xl bg-white/[0.04] border border-white/10 p-3">
                  <div className={`h-9 w-9 rounded-lg bg-gradient-to-br ${b.tint} flex items-center justify-center shrink-0 shadow-md`}>
                    <b.icon className="h-4 w-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-white">{b.title}</div>
                    <div className="text-[12px] text-white/70 leading-snug">{b.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                { stat: "12K+",  label: "shoppers"     },
                { stat: "1.2K",  label: "real reviews" },
                { stat: "160+",  label: "verified products" },
              ].map((s) => (
                <div key={s.label} className="rounded-xl bg-white/[0.06] backdrop-blur border border-white/10 p-3 text-center">
                  <div className="font-display text-2xl font-bold bg-gradient-to-br from-orange-400 to-fuchsia-400 bg-clip-text text-transparent">
                    {s.stat}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {variant === "login" && (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80 mb-3">Welcome back</div>
              <h2 className="font-display text-3xl sm:text-4xl xl:text-[2.5rem] font-bold tracking-tight leading-[1.1]">
                Today&apos;s <span className="bg-gradient-to-br from-orange-400 to-fuchsia-400 bg-clip-text text-transparent">honest score</span>{" "}
                is ready for you.
              </h2>
              <p className="text-white/70 leading-relaxed mt-3 text-[14px] max-w-md">
                We&apos;ve been busy — reading signals, catching intent, and quietly making sure every product on SociBaba™ earns its stripes.
              </p>
            </div>
            <SibaScoreHero />
            <LiveTicker />
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Zap,          stat: "1.6s", label: "avg score" },
                { icon: Star,         stat: "4.8",  label: "trust rating" },
                { icon: ShieldCheck,  stat: "99.9%", label: "uptime" },
              ].map((s) => (
                <div key={s.label} className="rounded-xl bg-white/[0.06] backdrop-blur border border-white/10 p-3">
                  <s.icon className="h-3.5 w-3.5 text-fuchsia-300 mb-1.5" />
                  <div className="font-display text-lg font-bold leading-none">{s.stat}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {variant === "forgot" && (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80 mb-3">Locked out? We&apos;ve got you</div>
              <h2 className="font-display text-3xl sm:text-4xl xl:text-[2.5rem] font-bold tracking-tight leading-[1.1]">
                Take a breath.<br />
                <span className="bg-gradient-to-br from-orange-400 to-fuchsia-400 bg-clip-text text-transparent">You&apos;re still one of us.</span>
              </h2>
              <p className="text-white/70 leading-relaxed mt-3 text-[14px] max-w-md">
                Reset links expire in 30 minutes — long enough to grab a chai, short enough to keep your account safe. While you&apos;re here:
              </p>
            </div>
            <SibaScoreHero label="SibaScore of the week" />
            <div className="rounded-xl bg-white/[0.04] border border-white/10 p-4 flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-fuchsia-300 shrink-0 mt-0.5" />
              <div className="text-[13px] text-white/80 leading-relaxed">
                <div className="font-semibold text-white mb-0.5">Did you know?</div>
                SociBaba™ never stores your password in plain text — even we can&apos;t read it. That&apos;s why you need a reset link, not a lookup.
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* Trust markers footer */}
      <div className="relative z-10 mt-8">
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Verified · Regulated · Yours</div>
        <TrustMarkers />
      </div>
    </div>
  );
};

export default AuthBrandPanel;
